import { createSelector } from 'reselect';
import { assoc, path, pick } from 'ramda';
import { commoditiesConfig, type CommodityConfigItem } from '@/utils/commodities';
import { Commodity } from '@enmon/commons/lib/shared';
import type { StaticRootState } from '@/reducers';
import type { CustomerMe, SerializedCustomerSettings } from '@enmon/commons/lib/enmon';
import type { GradientColorItem } from '@/components/Chart/colors/utils/getColorItemsByCommodity';
import type { StrictOmit } from '@enmon/commons';
import type { InvoiceDetailConfig } from '@enmon/commons/lib/enmon';
import { getCategoryForCommodity, type SupplyPointCategoryType } from '@enmon/commons/lib/enmon';
import { CONSUMPTION_COMMODITIES } from '@/utils/commodities';
import { getAppContext } from '../App/selectors';

export type AvailableCommoditiesShape = Partial<
  Record<Commodity, StrictOmit<CommodityConfigItem, 'color'> & { color: GradientColorItem }>
>;

const selectCustomerCommodities = (state: StaticRootState) =>
  path<Required<SerializedCustomerSettings['commodities']>>(['auth', 'customer', 'settings', 'commodities'], state);

/**
 * @deprecated DO NOT USE THIS SELECTOR EXCEPT IN `useAvailableCommodities` or `useAvailableConsumptionCommodities` HOOKS. Use those hooks instead.
 */
export const selectAvailableCommodities = createSelector(
  selectCustomerCommodities,
  getAppContext,
  (customersAssignedCommodities, appContext): AvailableCommoditiesShape =>
    appContext === 'magic'
      ? commoditiesConfig
      : (customersAssignedCommodities?.reduce<AvailableCommoditiesShape>((acc, commodity) => {
          const { name, units } = commodity ?? {};

          if (!name) return acc;
          if (units?.length === 0) return acc;

          return assoc(name, { ...commoditiesConfig[name], units }, acc);
        }, {}) ?? {}),
);

export type AvailableConsumptionCommoditiesShape = Partial<
  Record<
    (typeof CONSUMPTION_COMMODITIES)[number],
    StrictOmit<CommodityConfigItem, 'color'> & { color: GradientColorItem }
  >
>;

/**
 * @deprecated DO NOT USE THIS SELECTOR EXCEPT IN `useAvailableCommodities` or `useAvailableConsumptionCommodities` HOOKS. Use those hooks instead.
 */
export const selectAvailableConsumptionCommodities = createSelector(
  selectCustomerCommodities,
  getAppContext,
  (customersAssignedCommodities, appContext): AvailableConsumptionCommoditiesShape => {
    const consumptionCommodities = customersAssignedCommodities?.filter(({ name }) =>
      CONSUMPTION_COMMODITIES.some(consumptionType => consumptionType === name),
    );

    return appContext === 'magic'
      ? pick(CONSUMPTION_COMMODITIES, commoditiesConfig)
      : (consumptionCommodities?.reduce<AvailableConsumptionCommoditiesShape>((acc, commodity) => {
          const { name, units } = commodity ?? {};

          if (!name) return acc;
          if (units?.length === 0) return acc;

          return assoc(name, { ...commoditiesConfig[name], units }, acc);
        }, {}) ?? {});
  },
);

const getInvoiceDetailsConfig = (state: StaticRootState) => state.app.customerConfig?.invoiceDetails;

export type InvoiceDetailsByCategory = Partial<Record<SupplyPointCategoryType, readonly InvoiceDetailConfig[]>>;
export type InvoiceDetailsByCommodity = Partial<Record<Commodity, readonly InvoiceDetailConfig[]>>;

export const flattenBySupplyPointCategory = (
  detailsConfig?: CustomerMe['config']['invoiceDetails'],
): InvoiceDetailsByCategory => {
  if (!detailsConfig) return {};

  return Object.values(Commodity).reduce<InvoiceDetailsByCategory>((acc, commodity) => {
    const config = detailsConfig[commodity];

    // If it is array, put it under all categories for that commodity
    if (Array.isArray(config)) {
      const categories = Object.values(getCategoryForCommodity(commodity)) as SupplyPointCategoryType[];
      for (const category of categories) {
        acc[category] = config;
      }
    } else {
      // Otherwise it is already keyed by category, so just copy it over
      for (const [category, details] of Object.entries(config ?? {})) {
        acc[category as SupplyPointCategoryType] = details;
      }
    }

    return acc;
  }, {});
};

const flattenBySupplyPointCommodity = (
  detailsConfig?: CustomerMe['config']['invoiceDetails'],
): InvoiceDetailsByCommodity => {
  if (!detailsConfig) return {};

  return Object.values(Commodity).reduce<InvoiceDetailsByCommodity>((acc, commodity) => {
    const config = detailsConfig[commodity];

    // If it is array, put it under commodity
    if (Array.isArray(config)) {
      acc[commodity] = config;
    } else {
      // Otherwise merge details from all categories under that commodity
      const mergedDetails: InvoiceDetailConfig[] = [];
      for (const [_, details] of Object.entries(config ?? {})) {
        mergedDetails.push(...details);
      }
      acc[commodity] = mergedDetails;
    }

    return acc;
  }, {});
};

export const selectInvoiceDetailsConfigPerCategory = createSelector(
  getInvoiceDetailsConfig,
  flattenBySupplyPointCategory,
);

export const selectInvoiceDetailsConfigPerCommodity = createSelector(
  getInvoiceDetailsConfig,
  flattenBySupplyPointCommodity,
);

export const selectInvoiceDetailsConfig = createSelector(
  getInvoiceDetailsConfig,
  (detailsConfig): CustomerMe['config']['invoiceDetails'] => detailsConfig ?? {},
);
